import {
    AfterViewInit,
    Component,
    effect,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
    ViewChild
} from "@angular/core";
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";
import { MatMenuModule } from "@angular/material/menu";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { ChatNandaService } from "@app/shared/services/chat-nanda.service";
import { ContactService } from "@app/shared/services/contact.service";
import { DeviceService } from "@app/shared/services/device.service";
import { ScrollUtil } from "@arch/_shared/utils/scroll.util";
import { EventsService } from "@arch/domain/application/services/events.service";
import { RemoveChatSessionByUserUsecase } from "@arch/domain/application/usecase/remove-chat-session-by-user.usecase";
import { ReportEntity } from "@arch/domain/entity";
import { GetMessagesHistoryController } from "@arch/presentation/controllers/get-messages-history.controller";
import { GetSummaryByUserController } from "@arch/presentation/controllers/get-summary-by-user.controller";
import { ReplyToUseController } from "@arch/presentation/controllers/reply-to-user.controller";
import _ from "lodash";

@Component({
    selector: "app-nanda-chat",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        ReactiveFormsModule,
        MatMenuModule
    ],
    templateUrl: "./nanda-chat.component.html",
    styleUrl: "./nanda-chat.component.scss"
})
export class NandaChatComponent implements AfterViewInit {
    @ViewChild("messagesWrapper") messagesWrapper: ElementRef<HTMLDivElement>;
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: false }) bordeless: boolean = false;
    @Output()
    public onClose: EventEmitter<void> = new EventEmitter<void>();

    public readonly chatNandaService = inject(ChatNandaService);
    private readonly fb = inject(FormBuilder);
    private readonly replyToUserController = inject(ReplyToUseController);
    private readonly removeChatSessionByUserController = inject(
        RemoveChatSessionByUserUsecase
    );
    private readonly getSummaryByUserController = inject(
        GetSummaryByUserController
    );
    private readonly getMessagesHistoryController = inject(
        GetMessagesHistoryController
    );
    private readonly deviceService = inject(DeviceService);
    private readonly eventsService = inject(EventsService);
    private readonly contactService = inject(ContactService);

    public form: FormGroup = this.fb.group({
        message: ["", Validators.required]
    });
    public contact = this.contactService.get();
    public isOpened: boolean = false;
    public messages: Record<string, MessagesChatType[]> = {};
    public messagesDatesString: string[] = [];
    public initialMessage: string = "";
    public typingIndicator: boolean = false;
    public loading: boolean = false;
    private _loading = signal<boolean>(false);
    public isMobile = this.deviceService.getIsMobile();

    constructor() {
        effect(() => {
            const loading = this._loading();

            if (this.chatNandaService.isOpen && !loading) {
                setTimeout(() => {
                    this.resetScroll();

                    setTimeout(() => {
                        this._setScrollToNandaChat();
                    }, 50);
                });
            }
        });
    }

    async ngOnInit(): Promise<void> {
        this.loading = true;

        this._loading.set(true);

        await this.getHistoryMessages();
        await this.startChat();

        if (this.bordeless) {
            setTimeout(() => {
                this.scrollBoth();
            }, 500);
        }

        setTimeout(() => {}, 500);

        this.loading = false;
        this._loading.set(false);
    }

    private _setScrollToNandaChat(): void {
        const chat = this.messagesWrapper?.nativeElement;
        if (!chat) return;
        const elementsLink = chat.querySelectorAll("a");
        for (let target of Array.from(elementsLink)) {
            target.addEventListener("click", (event: any) => {
                this.onClose.emit();

                if (!this.bordeless) {
                    event.preventDefault();
                    const value = new URL(target.href).hash;
                    const previewElement = document.getElementById(
                        this.isMobile()
                            ? "ReportContentMobileScroll"
                            : "ReportPreviewScroll"
                    );

                    ScrollUtil.scrollTo({
                        elementParent: previewElement,
                        elementChild: document.getElementById(
                            value.replace("#", "")
                        )
                    });
                }
            });
        }
    }

    public handleCloseNanda(): void {
        if (!this.bordeless) {
            this.chatNandaService.close();
            return;
        }
        this.onClose.emit();
    }

    ngAfterViewInit(): void {}

    public async onClearMessages(): Promise<void> {
        this.loading = true;
        await this.removeChatSessionByUserController.execute({
            report: this.report,
            contact: this.contact,
            type: "report"
        });

        this.messages = {};
        this.messagesDatesString = [];
        this.loading = false;
    }

    private async startChat(): Promise<void> {
        const chat = await this.getSummaryByUserController.execute({
            type: "report",
            report: this.report,
            contact: this.contact,
            message: `Crie um resumo claro, direto e estratégico (com no máximo 3 parágrafos) com base no relatório informado. O objetivo é permitir que um empresário entenda rapidamente os pontos mais relevantes do seu desempenho recente.* 
            O resumo deve seguir a estrutura abaixo:
            o nome do meu Dash é ${this.report.props.name} a fonte do Dash é ${this.report.sourceName}
Informe o período analisado e o nome do Dash.
Destaque os principais indicadores analisados, com foco em impacto financeiro e operacional.
Aponte variações relevantes, tendências e oportunidades de melhoria ou crescimento.
Evite frases genéricas. Foque no que mudou, no que chama atenção e no que o empresário deve priorizar.
Exemplo de início, use as tags <p> e </p> para que o texto seja exibido como um parágrafo.
<p>Entre janeiro e junho de 2025, o {$nome do relatório} destacou um avanço expressivo na margem líquida e um alerta sobre o aumento do custo fixo.</p>`
        });

        this.initialMessage = this.replaceHrefs(chat.message);
        this._setScrollToNandaChat();
    }

    onToggleChat(): void {
        this.isOpened = !this.isOpened;
    }

    private getDateString(date: Date): string {
        return date.toLocaleDateString("pt-BR", {
            weekday: "short",
            day: "numeric",
            month: "long"
        });
    }

    public handleGetMessages(date: string): MessagesChatType[] {
        return this.messages[date] || [];
    }

    private async getHistoryMessages(): Promise<void> {
        const messages = await this.getMessagesHistoryController.execute({
            type: "report",
            report: this.report,
            contact: this.contact,
            limit: 50,
            page: 0
        });

        const list = messages.mensages.reverse().map((item) => ({
            message: this.replaceHrefs(item.message.split("\n").join("")),
            date: item.time,
            author: item.role === "user"
        }));

        const messagesGrouped: Record<string, MessagesChatType[]> = {};

        for (let message of list) {
            if (
                this.messagesDatesString.indexOf(
                    this.getDateString(message.date)
                ) < 0
            ) {
                this.messagesDatesString.push(this.getDateString(message.date));
            }
            if (!messagesGrouped[this.getDateString(message.date)]) {
                messagesGrouped[this.getDateString(message.date)] = [];
                messagesGrouped[this.getDateString(message.date)].push(message);
            } else {
                messagesGrouped[this.getDateString(message.date)].push(message);
            }
        }

        this.messages = messagesGrouped;

        // setTimeout(() => {
        //     this.resetScroll();
        // }, 50);
    }

    public onCopyMessage(message: string) {
        window.navigator.clipboard.writeText(message.replace(/<[^>]*>/g, ""));
    }

    public onVoiceMessage(message: string): void {
        const utterance = new SpeechSynthesisUtterance(
            message.replace(/<[^>]*>/g, "")
        );
        utterance.lang = "pt-BR";
        speechSynthesis.speak(utterance);
    }

    public async onSendMessage(message?: string): Promise<void> {
        if (this.typingIndicator) return;
        if (!message && !this.form.value.message.length) return;
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.conversar-nanda`
        );
        this.addMessage({
            message: message || this.form.value.message,
            author: true,
            date: new Date()
        });
        const currentMessage = message || _.cloneDeep(this.form.value.message);
        this.form.reset();
        this.scrollBoth();

        this.typingIndicator = true;
        this.setTypingIndicator(true);
        const response = await this.replyToUserController.execute({
            report: this.report,
            contact: this.contact,
            message: currentMessage,
            type: "report"
        });
        this.setTypingIndicator(false);
        this.addMessage({
            message: this.replaceHrefs(response.message),
            author: false,
            date: new Date()
        });

        this.typingIndicator = false;
        this._setScrollToNandaChat();
        this.scrollBoth();
    }

    private addMessage(message: MessagesChatType): void {
        if (
            this.messagesDatesString.indexOf(this.getDateString(message.date)) <
            0
        ) {
            this.messagesDatesString.push(this.getDateString(message.date));
        }

        if (!this.messages[this.getDateString(message.date)]) {
            this.messages[this.getDateString(message.date)] = [];
            this.messages[this.getDateString(message.date)].push(message);
        } else {
            this.messages[this.getDateString(message.date)].push(message);
        }
    }

    private resetScroll(): void {
        const element = this.messagesWrapper?.nativeElement;
        if (!element) return;
        element.scrollTo(0, element.scrollHeight);
    }

    private scrollBoth(): void {
        setTimeout(() => {
            const el = this.messagesWrapper?.nativeElement;
            if (!el) return;
            el.scrollTo({
                top: el.scrollHeight,
                left: el.scrollWidth,
                behavior: "smooth"
            });
        }, 2);
    }

    private replaceHrefs(texto: string): string {
        const baseUrl = window.location.href.replace(/#.*$/, "");
        const urlBase = baseUrl.replace("#visualization-{$id}", "");
        const regex = /href="#+([^"]+)"/g;
        return texto.replace(regex, (match, id) => {
            return `href="${urlBase}#visualization-${id}"`;
        });
    }

    private setTypingIndicator(status: boolean = true): void {
        if (status) {
            this.messagesDatesString.push("typing-event");
            this.scrollBoth();
        } else {
            if (this.messagesDatesString.indexOf("typing-event") >= 0) {
                this.messagesDatesString.splice(
                    this.messagesDatesString.indexOf("typing-event"),
                    1
                );
            }
        }
    }
}

type MessagesChatType = {
    author: boolean;
    message: string;
    date?: Date;
    dateString?: string;
};
